import React from "react";
import ReactDOM from "react-dom";
import { Provider } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";
import jwt_decode from "jwt-decode";
import { configureStore, persistor as getPersistor } from "./redux/store/configureStore";
import App from "./App";
import * as serviceWorker from "./serviceWorker";
import { refreshUser, logout, logoutUser } from "./redux/actions/session_actions";
import { setAuthToken, injectStore } from "./util/session_api_util";
import { Helmet } from "react-helmet";
import { LoadingProvider } from './LoadingContext';
import GlobalLoader from './GlobalLoader';
import axios from 'axios';
import apiClient from "./util/apiClient";

import './assets/styles/reset.css';
import './assets/styles/universal.css'; // Assuming you have this or similar base styles
import './assets/styles/dashboard.css';
import './assets/styles/faq.css';
import './assets/styles/feed.css';
import './assets/styles/feedback.css';
import './assets/styles/grid-styles.css';
import './assets/styles/header.css';
import './assets/styles/login.css';
import './assets/styles/management-content.css';
import './assets/styles/modal.css';
import './assets/styles/model-uploader.css';
import './assets/styles/notifications.css';
import './assets/styles/portal.css';
import './assets/styles/secondary-header.css';
import './assets/styles/management-profiles.css';
import './assets/styles/upload-form.css';
import './assets/styles/work-instructions-author.css';
import './assets/styles/work-instructions-page.css';
import './assets/styles/work-instructions-preview.css';
import './assets/styles/work-instruction-summary.css';
import './assets/styles/media-queries.css';
import './assets/styles/DirectionalButtons.css';
import './assets/styles/override.css';


// --- Font Awesome Library Setup ---
import { library } from '@fortawesome/fontawesome-svg-core';
import { fas } from '@fortawesome/free-solid-svg-icons';
import { far } from '@fortawesome/free-regular-svg-icons';
import { fab } from '@fortawesome/free-brands-svg-icons';
library.add(fas, far, fab);

// Check if the electronAPI is exposed by the preload script
const isElectron = !!window.electronAPI?.isElectron;

// Make the configuration setup async
async function initializeConfig() {
    let apiBaseUrl;
    let websocketUrl;

    if (isElectron) {
        console.log("[index.js] Electron detected. Requesting backend config...");
        try {
            const config = await window.electronAPI.getLocalBackendConfig();
            if (!config || !config.port) {
               throw new Error("Received invalid backend config from main process.");
            }
            const port = config.port;
            console.log(`[index.js] Received backend port from main process: ${port}`);
            apiBaseUrl = `http://localhost:${port}`; // Electron backend is always localhost from its perspective
            websocketUrl = `ws://localhost:${port}`;
        } catch (error) {
             console.error("[index.js] Failed to get backend config from Electron main process:", error);
             apiBaseUrl = `http://localhost:3001`; // Fallback for Electron if IPC fails
             websocketUrl = `ws://localhost:3001`;
             // Optionally, display an error to the user here, as this is a critical failure in Electron
             const root = document.getElementById("root");
             if (root) {
                 root.innerHTML = '<div style="color: red; padding: 20px;">Error: Could not connect to local backend services. Please restart the application.</div>';
             }
             throw error; // Re-throw to prevent further app initialization if critical for Electron
        }
    } else {
        console.log("[index.js] Web environment detected.");
        // The backend serves this page, so the API and websocket share its origin:
        // http://localhost:5000 locally, https://avaris.us in production.
        apiBaseUrl = window.location.origin;
        const websocketProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        websocketUrl = `${websocketProtocol}//${window.location.host}`;
    }

    console.log(`[index.js - initializeConfig] About to set window.config. Determined apiBaseUrl: ${apiBaseUrl}`);
    window.config = {
        getApiBaseUrl: () => apiBaseUrl,
        getWebsocketUrl: () => websocketUrl,
    };
    console.log(`[index.js - initializeConfig] window.config.getApiBaseUrl() immediately after set: ${window.config.getApiBaseUrl()}`);
    
    axios.defaults.baseURL = window.config.getApiBaseUrl();
    console.log(`[index.js - initializeConfig] axios.defaults.baseURL after set: ${axios.defaults.baseURL}`);

    return window.config;
}

// Function to render the React application
const renderApp = (storeInstance, persistorInstance) => {
  const root = document.getElementById("root");
  if (!root) {
      console.error("[index.js] Root DOM element not found. App cannot render.");
      return;
  }

  const AppContent = (
    <LoadingProvider>
      <Helmet>
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no"
        />
      </Helmet>
      <GlobalLoader />
      <App />
    </LoadingProvider>
  );

  ReactDOM.render(
    <Provider store={storeInstance}>
      {isElectron ? (
        AppContent
      ) : (
        <PersistGate loading={<div>loading</div>} persistor={persistorInstance}>
          {AppContent}
        </PersistGate>
      )}
    </Provider>,
    root
  );

  const initialHtmlLoader = document.getElementById('app-loading-overlay');
  if (initialHtmlLoader) {
    console.log('[index.js] Removing initial HTML loader after React render.');
    initialHtmlLoader.remove();
  }
};


document.addEventListener("DOMContentLoaded", async () => {
  console.log("[index.js] DOM Content Loaded. Initializing configuration...");
  try {
    const cfg = await initializeConfig(); // initializeConfig sets axios.defaults.baseURL and window.config
    console.log("[index.js] initializeConfig FINISHED.");
    
    // Explicitly set the baseURL for the imported apiClient instance
    // Use the value from window.config as it's the definitive source setup by initializeConfig
    if (cfg && typeof cfg.getApiBaseUrl === 'function') {
        apiClient.defaults.baseURL = cfg.getApiBaseUrl();
        console.log(`[index.js] apiClient.defaults.baseURL explicitly set to: ${apiClient.defaults.baseURL}`);
    } else {
        console.error("[index.js] Failed to set apiClient.defaults.baseURL because config or getApiBaseUrl is missing after initializeConfig.");
        // This is a critical failure point; consider how the app should behave.
        // For now, it will proceed, but API calls might fail or go to wrong URLs.
    }
    
    console.log(`[index.js] PRE-RENDER CHECK: window.config exists: ${!!window.config}`);
    if (window.config) {
        console.log(`[index.js] PRE-RENDER CHECK: window.config.getApiBaseUrl(): ${window.config.getApiBaseUrl()}`);
    }
    console.log(`[index.js] PRE-RENDER CHECK: axios.defaults.baseURL: ${axios.defaults.baseURL}`);
    
    console.log("[index.js] Configuration initialized successfully. Setting up Redux store and rendering app...");

    // All the preloadedState and store configuration logic remains the same
    let preloadedState = {};
    let decodedUser = null;
    let refreshTokenDecoded = null;
    let refreshTokenExp = null;

    if (localStorage.jwtToken) {
      setAuthToken(localStorage.jwtToken);
      try {
        decodedUser = jwt_decode(localStorage.jwtToken);
      } catch (e) {
          console.error("Invalid access token on load:", e);
          localStorage.removeItem("jwtToken");
          localStorage.removeItem("refreshToken");
          setAuthToken(false);
          decodedUser = null;
      }
      if (decodedUser && localStorage.refreshToken) { 
        try {
          refreshTokenDecoded = jwt_decode(localStorage.refreshToken);
          refreshTokenExp = refreshTokenDecoded.exp;
          console.log("[index.js] Preloaded refreshTokenExp:", refreshTokenExp);
        } catch (e) {
          console.warn("[index.js] Invalid refresh token on load:", e);
          refreshTokenExp = null;
        }
      }
      if (decodedUser) {
        preloadedState.session = { 
          isAuthenticated: true, 
          user: decodedUser, 
          refreshTokenExp: refreshTokenExp 
        };
      } else {
         preloadedState.session = { isAuthenticated: false, user: null, refreshTokenExp: null };
      }
    } else {
       preloadedState.session = { isAuthenticated: false, user: null, refreshTokenExp: null };
    }

    const store = configureStore(preloadedState);
    const appPersistor = getPersistor(store);
    injectStore(store);
    
    // Render the application
    renderApp(store, appPersistor);

    // Post-render logic (token expiry checks)
    if (preloadedState?.session?.isAuthenticated && decodedUser) {
      const currentTime = Date.now() / 1000;
      if (currentTime > decodedUser.exp) {
        console.log("[index.js] Access token expired on load. Checking refresh token validity...");
        if (refreshTokenExp && currentTime < refreshTokenExp) {
          console.log("[index.js] Refresh token valid. Dispatching refreshUser.");
          store.dispatch(refreshUser(localStorage.refreshToken));
        } else {
          console.warn("[index.js] Refresh token invalid/expired on load. Deferring logout.");
        }
      }
    }

    // Unregister service worker if in Electron, otherwise register for web
    if (isElectron) {
        console.log("[index.js] Running in Electron, ensuring SW is unregistered.");
        serviceWorker.unregister();
    } else if ('serviceWorker' in navigator) {
        console.log("[index.js] Web environment, registering service worker.");
        serviceWorker.register(); // Simplified registration
    }

  } catch (error) {
    console.error("[index.js] Critical error during app initialization:", error);
    // Error during initializeConfig already handles displaying a message for Electron.
    // For web, you might want to display a generic error message in the #root element.
    const rootElement = document.getElementById("root");
    if (rootElement && !isElectron) { // Only if not already handled by initializeConfig
        rootElement.innerHTML = '<div style="color: red; padding: 20px;">Application failed to start. Please try again later.</div>';
    }
    const initialHtmlLoader = document.getElementById('app-loading-overlay');
    if (initialHtmlLoader) {
        initialHtmlLoader.remove(); // Remove loader even on error
    }
  }
});